<template>
  <div class="complete-card">
    <!-- Confetti dots background -->
    <div class="confetti-bg" aria-hidden="true">
      <span v-for="i in 12" :key="i" class="confetti-dot" :style="confettiStyle(i)" />
    </div>

    <!-- Trophy / celebration icon -->
    <div class="complete-icon-ring">
      <div class="complete-icon-inner">
        <span class="text-3xl celebration-bounce">🎉</span>
      </div>
    </div>

    <h2 class="complete-heading">
      {{ isTopic ? 'Topic Complete!' : 'Lesson Complete!' }}
    </h2>

    <p class="complete-subtitle">
      You've {{ isTopic ? 'practiced' : 'finished' }} <strong>{{ title }}</strong>
    </p>

    <!-- Band badge -->
    <div class="flex justify-center mb-3">
      <span class="complete-badge" :class="badgeColor">
        <MTIcon :name="badgeIconName" :size="16" />
        {{ badgeLabel }}
      </span>
    </div>

    <!-- Stars -->
    <div class="complete-stars">
      <span v-for="i in starCount" :key="'star-'+i" class="star-filled" :style="{ animationDelay: (i * 0.15) + 's' }">★</span>
      <span v-for="i in (3 - starCount)" :key="'empty-'+i" class="star-empty">★</span>
    </div>

    <p class="complete-encourage">{{ encouragement }}</p>

    <div class="complete-actions">
      <button class="complete-btn complete-btn-primary" @click="$emit('next')">
        <MTIcon name="arrow-right" :size="16" />
        {{ isTopic ? 'Back to lesson' : 'Next lesson' }}
      </button>
      <button class="complete-btn complete-btn-outline" @click="$emit('repeat')">
        <MTIcon name="refresh-cw" :size="16" />
        Practice again
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import MTIcon from "@/components/ui/MTIcon.vue";

const props = defineProps({
  lessonTitle: String,
  title: String,
  band: String,
  type: {
    type: String,
    default: "lesson"
  }
});

defineEmits(["next", "repeat"]);

const isTopic = computed(() => props.type === "topic");
const title = computed(() => props.title || props.lessonTitle);

const badgeMap = {
  beginner: { label: "First Steps", iconName: "star", color: "badge-blue", stars: 1 },
  developing: { label: "Growing Learner", iconName: "sprout", color: "badge-green", stars: 1 },
  secure: { label: "Solid Skills", iconName: "book-open", color: "badge-yellow", stars: 2 },
  confident: { label: "Confident Thinker", iconName: "rocket", color: "badge-orange", stars: 3 },
  mastery: { label: "Mastery Achieved", iconName: "crown", color: "badge-purple", stars: 3 }
};

const badge = computed(() => badgeMap[props.band] || badgeMap.beginner);
const badgeLabel = computed(() => badge.value.label);
const badgeIconName = computed(() => badge.value.iconName);
const badgeColor = computed(() => badge.value.color);
const starCount = computed(() => badge.value.stars);

const encouragement = computed(() => {
  return {
    beginner: "Great start — every expert begins here.",
    developing: "You're making steady progress — keep it up!",
    secure: "Strong understanding — well done!",
    confident: "You're thinking confidently — excellent work!",
    mastery: "Outstanding — you've mastered this!"
  }[props.band] || "Well done!";
});

const confettiColors = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4'];
function confettiStyle(i) {
  const color = confettiColors[(i - 1) % confettiColors.length];
  const left = 8 + ((i * 37) % 84);
  const top = 5 + ((i * 23) % 40);
  const size = 4 + (i % 3) * 2;
  const delay = (i * 0.2) % 1.5;
  return {
    background: color,
    left: left + '%',
    top: top + '%',
    width: size + 'px',
    height: size + 'px',
    animationDelay: delay + 's'
  };
}
</script>

<style scoped>
.complete-card {
  position: relative;
  overflow: hidden;
  text-align: center;
  padding: 2.5rem 2rem 2rem;
  background: linear-gradient(
    135deg,
    var(--mt-card-bg) 0%,
    color-mix(in srgb, var(--mt-accent-blue-muted) 30%, var(--mt-card-bg)) 50%,
    color-mix(in srgb, var(--mt-accent-purple-muted, var(--mt-accent-blue-muted)) 20%, var(--mt-card-bg)) 100%
  );
}

/* Confetti dots */
.confetti-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.confetti-dot {
  position: absolute;
  border-radius: 50%;
  opacity: 0;
  animation: confetti-float 2s ease-out forwards;
}
@keyframes confetti-float {
  0% { opacity: 0; transform: translateY(10px) scale(0); }
  30% { opacity: 0.7; transform: translateY(-5px) scale(1.2); }
  100% { opacity: 0.25; transform: translateY(0) scale(1); }
}

/* Icon */
.complete-icon-ring {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}
.complete-icon-inner {
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 3px solid #fbbf24;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 6px rgba(251, 191, 36, 0.15), 0 4px 12px rgba(251, 191, 36, 0.2);
}
.celebration-bounce {
  animation: bounce-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both;
}
@keyframes bounce-in {
  0% { transform: scale(0); }
  60% { transform: scale(1.3); }
  100% { transform: scale(1); }
}

/* Text */
.complete-heading {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--mt-text);
  margin-bottom: 0.35rem;
}
.complete-subtitle {
  font-size: 1rem;
  color: var(--mt-text-muted);
  margin-bottom: 0.75rem;
}
.complete-subtitle strong {
  color: var(--mt-text);
}

/* Badge */
.complete-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 1rem;
  border-radius: 9999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: white;
}
.badge-blue { background: #3b82f6; }
.badge-green { background: #22c55e; }
.badge-yellow { background: #eab308; }
.badge-orange { background: #f97316; }
.badge-purple { background: #9333ea; }

/* Stars */
.complete-stars {
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}
.star-filled {
  color: #fbbf24;
  animation: star-pop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  filter: drop-shadow(0 1px 2px rgba(251, 191, 36, 0.4));
}
.star-empty {
  color: var(--mt-border);
  opacity: 0.4;
}
@keyframes star-pop {
  0% { transform: scale(0) rotate(-20deg); opacity: 0; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

/* Encouragement */
.complete-encourage {
  font-size: 0.875rem;
  color: var(--mt-text-muted);
  margin-bottom: 1.5rem;
}

/* Buttons */
.complete-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.complete-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.5rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
}
.complete-btn:active {
  transform: scale(0.97);
}
.complete-btn-primary {
  background: var(--mt-primary, #3b82f6);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}
.complete-btn-primary:hover {
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}
.complete-btn-secondary {
  background: var(--mt-card-bg);
  color: var(--mt-primary);
  border: 2px solid var(--mt-primary);
}
.complete-btn-secondary:hover {
  background: color-mix(in srgb, var(--mt-primary) 8%, var(--mt-card-bg));
}
.complete-btn-outline {
  background: var(--mt-card-bg);
  color: var(--mt-text);
  border: 2px solid var(--mt-border);
}
.complete-btn-outline:hover {
  border-color: var(--mt-text-muted);
  background: var(--mt-hover);
}
</style>
